import { useMemo } from 'react';
import { Box, Card, CardContent, Chip, CircularProgress, Typography, useTheme, alpha } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon } from '@mui/icons-material';

interface TimeSeriesDataPoint {
  timestamp: string;
  totalCount?: number | null;
  executedCount?: number | null;
  skippedCount?: number | null;
  successRate?: number | null;
}

interface CrawlerSuccessChartProps {
  data: TimeSeriesDataPoint[];
  loading?: boolean;
  error?: Error | null;
}

const CrawlerSuccessChart = ({ data, loading, error }: CrawlerSuccessChartProps) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { timestamps: [], successRates: [] };

    return {
      timestamps: data.map(point => new Date(point.timestamp)),
      successRates: data.map(point => point.successRate ?? 0),
      executedCounts: data.map(point => point.executedCount ?? 0),
    };
  }, [data]);

  const currentRate = data.length > 0 ? (data[data.length - 1]?.successRate ?? 0) : 0;
  const previousRate = data.length > 1 ? (data[data.length - 2]?.successRate ?? 0) : currentRate;
  const rateTrend = currentRate - previousRate;
  const isPositiveTrend = rateTrend > 0;

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={350}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={350}>
            <Typography variant="h6" color="error" gutterBottom>
              Error loading chart data
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error.message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 400 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={350}>
            <Typography variant="h6" color="text.secondary">
              No data available
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Crawler timeline will appear here once data is collected
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 400,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary.dark">
              Crawler Success Rate Timeline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Success rate over time with trend indicator
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {currentRate.toFixed(1)}%
            </Typography>
            <Chip
              size="small"
              icon={isPositiveTrend ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${isPositiveTrend ? '+' : ''}${rateTrend.toFixed(1)}%`}
              sx={{
                backgroundColor: alpha(isPositiveTrend ? '#4CAF50' : '#F44336', 0.1),
                color: isPositiveTrend ? '#2E7D32' : '#C62828',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: isPositiveTrend ? '#2E7D32' : '#C62828',
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ width: '100%', height: 350 }}>
          <LineChart
            xAxis={[
              {
                data: chartData.timestamps,
                scaleType: 'time',
                valueFormatter: value => {
                  const date = value as Date;
                  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
                },
              },
            ]}
            yAxis={[
              {
                min: 0,
                max: 100,
                valueFormatter: value => `${value}%`,
              },
            ]}
            series={[
              {
                data: chartData.successRates,
                label: 'Success Rate',
                color: theme.palette.primary.main,
                showMark: true,
                curve: 'monotoneX',
              },
            ]}
            margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
            sx={{
              '& .MuiLineElement-root': {
                strokeWidth: 3,
              },
              '& .MuiMarkElement-root': {
                scale: '0.8',
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default CrawlerSuccessChart;
